import { CSSProperties } from "react";
import { ComponentProps } from "../registry";
import { useBound } from "../helpers";
import { BoundValue } from "../../types";

/**
 * Image renders an <img> with the catalog v1 fields:
 *   - url       (string)        — the image source
 *   - altText   (string)        — accessibility text
 *   - fit       (string)        — cover | contain | fill | scaleDown
 *   - maxWidth  (string | number) — optional CSS max-width ("140px", "25%", 140)
 *
 * `maxWidth` is an additive catalog v1 field — surfaces that don't set it
 * behave exactly as before (intrinsic width, capped by .a2-image's
 * max-width: 100% from the container). Surfaces that DO set it cap the
 * image to that width while preserving fit / aspect-ratio. Useful for
 * logos in flex columns where the default `align-items: stretch` would
 * otherwise blow the image up to full column width.
 */
export function Image({ fields, scope }: ComponentProps) {
  const url = useBound<string>(fields.url as BoundValue, scope);
  const alt = useBound<string>(fields.altText as BoundValue, scope);
  const fit = useBound<string>(fields.fit as BoundValue, scope) ?? "cover";
  const maxWidthRaw = useBound<string | number>(fields.maxWidth as BoundValue, scope);
  if (!url) return null;
  const style: CSSProperties | undefined =
    maxWidthRaw !== undefined && maxWidthRaw !== null && maxWidthRaw !== ""
      ? { maxWidth: typeof maxWidthRaw === "number" ? `${maxWidthRaw}px` : maxWidthRaw }
      : undefined;
  return <img className={`a2-image fit-${fit}`} src={url} alt={alt ?? ""} style={style} />;
}
